import { useCallback, useState, useEffect } from 'react';

const createUseStorage = (storage: Storage) => (key: string, initialValue: string) => {
  const getValue = useCallback(() => {
    const item = storage.getItem(key);
    return item === null ? initialValue : item;
  }, [initialValue, key]);

  const [storedValue, setStoredValue] = useState(getValue);

  const setValue = useCallback(
    (value: string) => {
      setStoredValue(value);
      try {
        storage.setItem(key, value);
      } catch (error) {
        // no-op
      }
    },
    [key],
  );

  useEffect(() => {
    const listener = () => {
      const newValue = getValue();

      if (newValue !== storedValue) {
        setStoredValue(newValue);
      }
    };

    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [getValue, key, storedValue]);

  return [storedValue, setValue, getValue] as const;
};

export const useLocalStorage = createUseStorage(window.localStorage);

export const useSessionStorage = createUseStorage(window.sessionStorage);
